package team.platforma.multiplatform_material_extension.calendar.calendar_period.core_ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.DialogProperties
import kotlinx.datetime.LocalDate
import materialextension.multiplatform_material_extension.generated.resources.Res
import materialextension.multiplatform_material_extension.generated.resources.mel_arrow_right
import materialextension.multiplatform_material_extension.generated.resources.mel_broom
import materialextension.multiplatform_material_extension.generated.resources.mel_friday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_monday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_saturday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_select_month
import materialextension.multiplatform_material_extension.generated.resources.mel_select_year
import materialextension.multiplatform_material_extension.generated.resources.mel_sunday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_thursday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_tuesday_short
import materialextension.multiplatform_material_extension.generated.resources.mel_wednesday_short
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import team.platforma.multiplatform_material_extension.calendar.core.CALENDAR
import team.platforma.multiplatform_material_extension.calendar.style.CalendarViewStyle
import team.platforma.multiplatform_material_extension.calendar.properties.CalendarViewProperties
import team.platforma.multiplatform_material_extension.calendar.core.core_ui.ChangeMonthView
import team.platforma.multiplatform_material_extension.calendar.core.core_ui.ChangeYearView
import team.platforma.multiplatform_material_extension.calendar.utils.AnimatedMonthChange
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarBadges
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarRange
import team.platforma.multiplatform_material_extension.calendar.utils.isNextMonthInRange
import team.platforma.multiplatform_material_extension.calendar.utils.isPreviousMonthInRange
import team.platforma.multiplatform_material_extension.calendar.utils.rememberAnimatedMonthChange
import team.platforma.multiplatform_material_extension.calendar.utils.setNextMonth
import team.platforma.multiplatform_material_extension.calendar.utils.setPreviousMonth
import team.platforma.multiplatform_material_extension.ui.dialog.CommonDialog
import team.platforma.multiplatform_material_extension.ui.dialog.style.CommonDialogStyle
import team.platforma.multiplatform_material_extension.ui.dialog.style.DialogStyle
import team.platforma.multiplatform_material_extension.util.gestures.HorizontalGestures

@Composable
internal fun CalendarPeriodImpl(
    modifier: Modifier,
    initFirstDate: LocalDate,
    initSecondDate: LocalDate,
    properties: CalendarViewProperties,
    onMonthYearChange: (LocalDate) -> Unit,
    calendarRange: CalendarRange,
    horizontalGestures: HorizontalGestures?,
    calendarBadges: CalendarBadges?,
    onPeriodSelected: (firstDate: LocalDate?, secondDate: LocalDate?) -> Unit,
    customStyle: DialogStyle?,
    clearPeriod: Boolean,
    onPeriodCleared: () -> Unit,
    showClearButton: Boolean
) {

    var selectedMonthYear by remember { mutableStateOf(initFirstDate) }

    var changeYear by remember { mutableStateOf(false) }
    var changeMonth by remember { mutableStateOf(false) }

    var mClearPeriod by remember { mutableStateOf(false) }

    var mPeriodStartDate by remember { mutableStateOf<LocalDate?>(initFirstDate) }
    var mPeriodEndDate by remember { mutableStateOf<LocalDate?>(initSecondDate) }

    var periodSetup by remember { mutableStateOf(false) }

    LaunchedEffect(mPeriodStartDate, mPeriodEndDate) {
        periodSetup = mPeriodStartDate != null && mPeriodEndDate != null
    }

    LaunchedEffect(clearPeriod) {
        mClearPeriod = clearPeriod
    }

    val animatedMonthChange: AnimatedMonthChange? =
        if (properties.animateClickChangeButtonMonth) rememberAnimatedMonthChange() else null

    Column(
        modifier = modifier
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(
                enabled = isPreviousMonthInRange(selectedMonthYear, calendarRange),
                onClick = {
                    if (properties.animateClickChangeButtonMonth) {
                        animatedMonthChange?.onFadeoutEnd {
                            selectedMonthYear = setPreviousMonth(selectedMonthYear)
                            onMonthYearChange(selectedMonthYear)
                        }
                    } else {
                        selectedMonthYear = setPreviousMonth(selectedMonthYear)
                        onMonthYearChange(selectedMonthYear)
                    }
                }) {
                Icon(
                    painter = painterResource(Res.drawable.mel_arrow_right),
                    contentDescription = "Previous Month",
                    modifier = Modifier.graphicsLayer(
                        rotationZ = 180f
                    )
                )
            }

            Row {
                TextButton(
                    onClick = {
                        changeMonth = !changeMonth
                    }
                ) {
                    Text(
                        text = CALENDAR().getMonthByNumber(selectedMonthYear.monthNumber),
                        fontSize = 18.sp,
                        color = CalendarViewStyle.monthStyle().color
                    )
                }

                TextButton(onClick = {
                    changeYear = !changeYear
                }) {
                    Text(
                        text = selectedMonthYear.year.toString(),
                        fontSize = 18.sp,
                        color = CalendarViewStyle.monthStyle().color
                    )
                }
                if (showClearButton) {
                    IconButton(
                        enabled = periodSetup,
                        onClick = {
                            mClearPeriod = !mClearPeriod
                        }
                    ) {
                        Icon(
                            painterResource(Res.drawable.mel_broom),
                            contentDescription = "Clear"
                        )
                    }
                }

            }

            IconButton(
                enabled = isNextMonthInRange(selectedMonthYear, calendarRange),
                onClick = {
                    if (properties.animateClickChangeButtonMonth) {
                        animatedMonthChange?.onFadeoutEnd {
                            selectedMonthYear = setNextMonth(selectedMonthYear)
                            onMonthYearChange(selectedMonthYear)
                        }
                    } else {
                        selectedMonthYear = setNextMonth(selectedMonthYear)
                        onMonthYearChange(selectedMonthYear)
                    }

                }) {
                Icon(
                    painterResource(Res.drawable.mel_arrow_right),
                    contentDescription = "Next Month"
                )
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceAround
        ) {
            val daysOfWeek = listOf<String>(
                stringResource(Res.string.mel_monday_short),
                stringResource(Res.string.mel_tuesday_short),
                stringResource(Res.string.mel_wednesday_short),
                stringResource(Res.string.mel_thursday_short),
                stringResource(Res.string.mel_friday_short),
                stringResource(Res.string.mel_saturday_short),
                stringResource(Res.string.mel_sunday_short)
            )

            daysOfWeek.forEach { day ->
                Text(
                    text = day,
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Medium,
                    color = CalendarViewStyle.dateStyle().enabledDateText,
                    fontSize = 16.sp
                )
            }
        }
        CalendarPeriodDatesView(
            initFirstDate = initFirstDate,
            initSecondDate = initSecondDate,
            year = selectedMonthYear.year,
            month = selectedMonthYear.monthNumber,
            onPeriodSelected = { firstDate, secondDate ->
                onPeriodSelected(firstDate, secondDate)
                mPeriodStartDate = firstDate
                mPeriodEndDate = secondDate
            },
            calendarRange = calendarRange,
            onPreviousMonth = {
                if (isPreviousMonthInRange(selectedMonthYear, calendarRange)) {
                    selectedMonthYear = setPreviousMonth(selectedMonthYear)
                    onMonthYearChange(selectedMonthYear)
                }
            },
            onNextMonth = {
                if (isNextMonthInRange(selectedMonthYear, calendarRange)) {
                    selectedMonthYear = setNextMonth(selectedMonthYear)
                    onMonthYearChange(selectedMonthYear)
                }
            },
            adaptiveHeight = properties.adaptiveHeight,
            calendarBadges = calendarBadges,
            horizontalGestures = horizontalGestures,
            animatedMonthChange = animatedMonthChange,
            animateBadge = properties.animateBadge,
            clearPeriod = mClearPeriod,
            onPeriodCleared = {
                onPeriodCleared()
                mClearPeriod = false
            }
        )

        if (changeYear) {
            CommonDialog(
                onDismiss = { changeYear = false },
                title = {
                    Text(
                        text = stringResource(Res.string.mel_select_year),
                        modifier = Modifier.padding(vertical = 8.dp),
                        fontWeight = FontWeight.Medium,
                        color = customStyle?.titleColor
                            ?: CommonDialogStyle.dialogStyle().titleColor
                    )
                },
                properties = DialogProperties(
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true,
                    usePlatformDefaultWidth = false
                ),
                customStyle = customStyle,
                changeVisibleState = false
            ) {
                ChangeYearView(
                    selectedDate = selectedMonthYear,
                    onDateSelected = { localDate ->
                        changeYear = false
                        selectedMonthYear = localDate
                        onMonthYearChange(selectedMonthYear)
                    },
                    calendarRange = calendarRange
                )
            }
        }

        if (changeMonth) {
            CommonDialog(
                onDismiss = { changeMonth = false },
                title = {
                    Text(
                        text = stringResource(Res.string.mel_select_month),
                        modifier = Modifier.padding(vertical = 8.dp),
                        fontWeight = FontWeight.Medium,
                        color = customStyle?.titleColor
                            ?: CommonDialogStyle.dialogStyle().titleColor
                    )
                },
                properties = DialogProperties(
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true,
                    usePlatformDefaultWidth = false
                ),
                customStyle = customStyle,
                changeVisibleState = false
            ) {
                ChangeMonthView(
                    selectedDate = selectedMonthYear,
                    onDateSelected = { localDate ->
                        changeMonth = false
                        selectedMonthYear = localDate
                        onMonthYearChange(selectedMonthYear)
                    },
                    calendarRange = calendarRange
                )
            }
        }


    }

}